import type { Message } from "@ag-ui/client";
import type { Agent, ConversationAgentSummary } from "~/services/agents";
import type { ProjectConversation } from "~/services/projects";

// The composer's active agent for a conversation's next turn, persisted in
// sessionStorage: it survives a refresh and clears when the tab closes.
const nextTurnAgentStorageKey = (conversationId: string) => `next-turn-agent:${conversationId}`;

export const readNextTurnAgent = (conversationId: string): string | null => {
  try {
    return sessionStorage.getItem(nextTurnAgentStorageKey(conversationId));
  } catch {
    return null;
  }
};

export const writeNextTurnAgent = (conversationId: string, agentId: string) => {
  try {
    sessionStorage.setItem(nextTurnAgentStorageKey(conversationId), agentId);
  } catch {
    // Storage unavailable (e.g. private browsing) — the selection just won't
    // survive a refresh.
  }
};

// A turn's opening user message, as /connect serves it — names the turn's
// agent via `agentId` when the turn has one.
function namesTurnAgent(message: Message): message is Message & { agentId: string } {
  return message.role === "user" && "agentId" in message && typeof message.agentId === "string";
}

// The agent of the latest turn that names one — a turn without an agent is
// skipped, so an agentless turn does not clear the effective current agent.
export function latestTurnAgentId(messages: readonly Message[]): string | null {
  return messages.findLast(namesTurnAgent)?.agentId ?? null;
}

// The distinct agents that handled the conversation's turns, in order of first
// use; a turn without an agent is skipped.
export function turnAgentIds(messages: readonly Message[]): string[] {
  return [...new Set(messages.filter(namesTurnAgent).map((message) => message.agentId))];
}

// A turn's opening user message, as the shared and admin views serve it — names
// the turn's agent summary via `turnAgent` when the turn has one.
function namesTurnAgentSummary(
  message: Message
): message is Message & { turnAgent: ConversationAgentSummary } {
  return message.role === "user" && "turnAgent" in message && message.turnAgent != null;
}

// The named agent's summary, from a turn it served, for views without an agent
// roster to resolve a bare id against. Null when no turn names that agent.
export function turnAgentSummary(
  messages: readonly Message[],
  agentId: string
): ConversationAgentSummary | null {
  return (
    messages.filter(namesTurnAgentSummary).find((m) => m.turnAgent.id === agentId)?.turnAgent ??
    null
  );
}

// The project's default agent: the agent of the latest session's most recent
// turn that named one (`latest_agent_id`; after A -> B -> A, A), if still
// usable, else the first usable agent. Null when none is usable.
export function defaultSessionAgentId(
  usableAgents: readonly Pick<Agent, "id">[],
  sessions: readonly Pick<ProjectConversation, "latest_agent_id">[]
): string | null {
  const lastUsedAgentId = sessions.find((s) => s.latest_agent_id != null)?.latest_agent_id;
  return usableAgents.find((a) => a.id === lastUsedAgentId)?.id ?? usableAgents[0]?.id ?? null;
}
